import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, ArrowUpRight } from "lucide-react";
import dauphinCosmiqueImg from "@/assets/generated/dauphin-cosmique.inline";
import bancDauphinsImg from "@/assets/generated/banc-dauphins.inline";
import vibeQuantiqueImg from "@/assets/generated/vibe-quantique.inline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback image for offers without one
const OFFER_FALLBACK = {
    "Fréquence offerte": dauphinCosmiqueImg,
    "École des Maîtres Dauphins": bancDauphinsImg,
    "Accordeur Quantique": vibeQuantiqueImg,
};

const OffersSection = () => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/offers`)
            .then((r) => mounted && setOffers(r.data || []))
            .catch((e) => mounted && setErr(e?.message || "Erreur"))
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, []);

    return (
        <section
            id="offres"
            data-testid="offers-section"
            className="relative py-20 sm:py-24 md:py-32 overflow-hidden bg-organic-deep grain"
        >
            <div className="halo bg-champagne/15 w-[420px] h-[420px] -top-20 right-[-100px]" />
            <div className="halo bg-bronze/15 w-[360px] h-[360px] bottom-0 left-[-80px]" />

            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 md:px-12">

                {/* Champagne luxury frame wraps the whole section content */}
                <div className="champagne-frame p-5 sm:p-8 md:p-14">

                    <div className="flex items-center gap-3 mb-5">
                        <Sparkles className="w-4 h-4 text-champagne" />
                        <span className="text-[11px] font-mono-tracked text-champagne/90">
                            06 · Portails du moment
                        </span>
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl md:text-6xl text-ivory leading-[1.05] mb-4 sm:mb-5 font-light">
                        Offres &{" "}
                        <span className="italic text-champagne-grad">portails</span>
                        <br className="hidden md:block" />
                        <span className="text-ivory/70"> du moment</span>
                    </h2>
                    <p className="text-ivory/65 max-w-xl text-sm sm:text-base md:text-lg leading-relaxed mb-10 md:mb-14">
                        Une sélection vivante : promotions, sessions ouvertes, webinaires
                        et passages saisonniers.
                    </p>

                    {loading && (
                        <div className="text-ivory/40 text-sm font-mono-tracked" data-testid="offers-loading">
                            chargement…
                        </div>
                    )}
                    {err && !loading && (
                        <div className="text-ivory/40 text-sm" data-testid="offers-error">
                            Les portails se rechargent. Revenez bientôt.
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-5 md:gap-7" data-testid="offers-grid">
                        {offers.map((o, idx) => {
                            const img = o.image || OFFER_FALLBACK[o.title] || dauphinCosmiqueImg;
                            return (
                                <a
                                    key={o.id}
                                    href={o.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group relative overflow-hidden rounded-3xl glass p-6 sm:p-7 md:p-9 transition-all duration-700 hover:-translate-y-1"
                                    data-testid={`offer-card-${idx}`}
                                >
                                    <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-40 bg-gradient-to-br from-champagne/40 to-bronze/10 group-hover:opacity-70 transition-opacity duration-700" />

                                    <div className="relative flex items-start gap-4 sm:gap-5 mb-5">
                                        {/* Circle image */}
                                        <div className="shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border border-champagne/40 ring-1 ring-champagne/10 ring-offset-2 ring-offset-ink-900/0">
                                            <img
                                                src={img}
                                                alt={o.title}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                loading="lazy"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0 pt-1">
                                            <div className="flex items-center justify-between gap-3 mb-1.5">
                                                <span className="text-[10px] font-mono-tracked text-champagne/85">
                                                    {String(idx + 1).padStart(2, "0")} · Portail
                                                </span>
                                                {o.badge && (
                                                    <span className="text-[10px] font-mono-tracked text-ink-900 bg-champagne rounded-full px-2.5 py-1 whitespace-nowrap">
                                                        {o.badge}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="font-display text-xl sm:text-2xl md:text-3xl text-ivory leading-tight font-light">
                                                {o.title}
                                            </h3>
                                        </div>
                                    </div>

                                    <p className="relative text-sm md:text-base text-ivory/70 leading-relaxed mb-6">
                                        {o.description}
                                    </p>

                                    <div className="relative flex items-center gap-2 text-champagne text-sm font-mono-tracked">
                                        Entrer
                                        <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OffersSection;
